import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import AdminShell from '../../components/admin/AdminShell';
import { adminV2Api } from '../../lib/api';
import {
  Plus, Edit3, Calendar, MapPin, Loader2, Search, Eye, EyeOff,
  AlertCircle, Users, Sparkles, ChevronLeft, ChevronRight, Trash2,
} from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';

/**
 * /admin/events — V2 admin event list.
 *
 * Shows: title, date, status badge, inventory sold, edit btn, quick publish toggle.
 */

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso + 'T00:00:00').toLocaleDateString('en-IN', {
      weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
    });
  } catch { return iso; }
};

const STATUS_COLORS = {
  draft:      'bg-zinc-500/15 text-zinc-300 border-zinc-400/40',
  published:  'bg-emerald-500/15 text-emerald-300 border-emerald-400/40',
  completed:  'bg-slate-500/15 text-slate-300 border-slate-400/40',
};

const computeInventorySold = (event) => {
  let total = 0, sold = 0;
  for (const pt of event.pass_types || []) {
    total += pt.inventory_total || 0;
    sold  += (pt.inventory_total || 0) - (pt.inventory_remaining || 0);
  }
  return { total, sold, pct: total > 0 ? Math.round((sold / total) * 100) : 0 };
};


export default function AdminEventsPage() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [publishingId, setPublishingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);   // event object pending deletion
  const [deleting, setDeleting] = useState(false);
  const LIMIT = 24;

  const fetchEvents = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = { limit: LIMIT, skip: (page - 1) * LIMIT, include_inactive: true };
      if (statusFilter !== 'all') params.status = statusFilter;
      const res = await adminV2Api.listEvents(params);
      setEvents(res.data?.events || []);
      setTotal(res.data?.total || 0);
    } catch (e) {
      setError(e?.message || 'Failed to load events');
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const togglePublish = async (event) => {
    setPublishingId(event._id);
    const next = event.status === 'published' ? 'draft' : 'published';
    try {
      await adminV2Api.updateEvent(event._id, { status: next });
      toast.success(`Event ${next === 'published' ? 'published' : 'moved to draft'}`);
      await fetchEvents();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || 'Could not update event');
    } finally {
      setPublishingId(null);
    }
  };

  // Hard-delete: physically removes the event document so it disappears from
  // /admin/events. Bookings tied to the event keep their event_id reference
  // but won't resolve, which is the intended behaviour for clearing
  // test/draft events that should never be listed again.
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await adminV2Api.deleteEvent(deleteTarget._id, { hard: true });
      toast.success(`"${deleteTarget.title}" removed permanently`);
      setDeleteTarget(null);
      // If we just removed the last row on this page, step back one page
      if (events.length === 1 && page > 1) setPage((p) => Math.max(1, p - 1));
      else await fetchEvents();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Could not delete event'));
    } finally {
      setDeleting(false);
    }
  };

  const filtered = search.trim()
    ? events.filter((e) => e.title?.toLowerCase().includes(search.toLowerCase().trim()))
    : events;

  const pages = Math.max(1, Math.ceil(total / LIMIT));

  return (
    <AdminShell title="Events" subtitle="V2 event manager — create, publish, and track inventory">
      <div className="admin-events-page" data-testid="admin-events-page">
        {/* Toolbar */}
        <div className="admin-events-toolbar">
          <div className="admin-events-search">
            <Search size={14} className="text-zinc-500" />
            <input
              type="text"
              placeholder="Search by title"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="admin-events-search"
            />
          </div>
          <div className="admin-events-filters">
            {['all', 'draft', 'published', 'completed'].map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => { setPage(1); setStatusFilter(s); }}
                className={`filter-pill ${statusFilter === s ? 'filter-pill-active' : ''}`}
                data-testid={`admin-events-filter-${s}`}
              >
                {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="admin-events-new-btn"
            onClick={() => navigate('/admin/events/new')}
            data-testid="admin-events-new-btn"
          >
            <Plus size={14} /> New Event
          </button>
        </div>

        {loading ? (
          <div className="admin-events-loading" data-testid="admin-events-loading">
            <Loader2 size={24} className="animate-spin text-[var(--accent-gold)]" />
            <p>Loading events…</p>
          </div>
        ) : error ? (
          <div className="admin-events-error">
            <AlertCircle size={24} className="text-red-400 mb-2" />
            <p>{error}</p>
            <button className="btn-gold mt-3" onClick={fetchEvents}>Retry</button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="admin-events-empty" data-testid="admin-events-empty">
            <Sparkles size={36} className="text-[var(--accent-gold)] mb-3" />
            <h3 className="text-lg text-white font-semibold mb-2">No events yet</h3>
            <p className="text-zinc-400 text-sm mb-5">Get started by creating your first event.</p>
            <button
              type="button"
              className="btn-gold"
              onClick={() => navigate('/admin/events/new')}
              data-testid="admin-events-create-first"
            >
              <Plus size={14} /> Create Event
            </button>
          </div>
        ) : (
          <>
            <div className="admin-events-table" data-testid="admin-events-table">
              <div className="admin-events-row admin-events-row-head">
                <div className="col-event">Event</div>
                <div className="col-date">Date</div>
                <div className="col-status">Status</div>
                <div className="col-inventory">Inventory</div>
                <div className="col-actions">Actions</div>
              </div>
              {filtered.map((event) => {
                const inv = computeInventorySold(event);
                return (
                  <motion.div
                    key={event._id}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.22 }}
                    className="admin-events-row"
                    data-testid={`admin-event-row-${event.slug}`}
                  >
                    <div className="col-event">
                      <div className="admin-event-title">{event.title}</div>
                      {event.venue && (
                        <div className="admin-event-venue">
                          <MapPin size={10} className="text-zinc-500" />
                          <span>{event.venue}</span>
                        </div>
                      )}
                    </div>
                    <div className="col-date">
                      <Calendar size={12} className="text-[var(--accent-gold)] mr-1.5" />
                      {formatDate(event.event_date)}
                      {event.event_time && <span className="text-zinc-500 ml-1.5">· {event.event_time}</span>}
                    </div>
                    <div className="col-status">
                      <span
                        className={`status-pill ${STATUS_COLORS[event.status] || STATUS_COLORS.draft}`}
                        data-testid={`admin-event-status-${event.slug}`}
                      >
                        {(event.status || 'draft').toUpperCase()}
                      </span>
                      {event.is_active === false && (
                        <span className="status-pill bg-red-500/15 text-red-300 border-red-400/40 ml-2">DELETED</span>
                      )}
                    </div>
                    <div className="col-inventory">
                      <div className="inv-text">
                        <Users size={11} className="text-zinc-500" />
                        <span data-testid={`admin-event-sold-${event.slug}`}>
                          {inv.sold} <span className="text-zinc-500">/ {inv.total}</span>
                        </span>
                      </div>
                      <div className="inv-bar">
                        <div className="inv-bar-fill" style={{ width: `${inv.pct}%` }} />
                      </div>
                    </div>
                    <div className="col-actions">
                      <button
                        type="button"
                        className="admin-icon-btn"
                        title={event.status === 'published' ? 'Unpublish' : 'Publish'}
                        onClick={() => togglePublish(event)}
                        disabled={publishingId === event._id}
                        data-testid={`admin-event-toggle-${event.slug}`}
                      >
                        {publishingId === event._id ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : event.status === 'published' ? (
                          <EyeOff size={14} />
                        ) : (
                          <Eye size={14} />
                        )}
                      </button>
                      <Link
                        to={`/admin/events/${event._id}/edit`}
                        className="admin-icon-btn admin-icon-btn-primary"
                        title="Edit"
                        data-testid={`admin-event-edit-${event.slug}`}
                      >
                        <Edit3 size={14} />
                      </Link>
                      <button
                        type="button"
                        className="admin-icon-btn admin-icon-btn-danger"
                        title="Delete event"
                        onClick={() => setDeleteTarget(event)}
                        data-testid={`admin-event-delete-${event.slug}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {pages > 1 && (
              <div className="admin-pagination">
                <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} className="bookings-page-btn">
                  <ChevronLeft size={14} /> Prev
                </button>
                <span className="text-xs text-zinc-500">Page {page} of {pages}</span>
                <button onClick={() => setPage((p) => Math.min(pages, p + 1))} disabled={page >= pages} className="bookings-page-btn">
                  Next <ChevronRight size={14} />
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Hard-delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && !deleting && setDeleteTarget(null)}>
        <AlertDialogContent
          className="bg-[#0E0E0E] border-[#2A2A2A] text-[#F2F2F2]"
          data-testid="admin-event-delete-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="font-['EB_Garamond'] text-2xl flex items-center gap-2 text-[#F2F2F2]">
              <Trash2 className="text-[#FF6B6B]" size={20} />
              Delete event permanently?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#B9B9B9] space-y-2">
              <span className="block">
                This will <span className="text-[#FFD7D7] font-medium">remove
                &ldquo;{deleteTarget?.title}&rdquo;</span> from the events list. The
                action cannot be undone.
              </span>
              <span className="block text-xs text-[#6F6F6F]">
                Existing bookings will keep their reference but will no longer resolve to a live event.
                For published events with live bookings, prefer &ldquo;Unpublish&rdquo; instead.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel
              disabled={deleting}
              className="border-[#2A2A2A] bg-transparent text-[#B9B9B9] hover:bg-[#161616]"
              data-testid="admin-event-delete-cancel"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmDelete(); }}
              disabled={deleting}
              className="bg-[#B83232] text-white hover:bg-[#9C2828] gap-2"
              data-testid="admin-event-delete-confirm"
            >
              {deleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
              {deleting ? 'Deleting…' : 'Delete permanently'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminShell>
  );
}
